import { useEffect, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Bell, CaretRight, Crown, Gear, Headphones, MusicNotes, SignOut, User } from '@phosphor-icons/react';
import { PASS_PLANS } from '../shared/data/constants';
import type { NoticeItem } from '../shared/data/constants';
import {
  drumActivePlanAtom,
  drumLanguageAtom,
  drumLoggedInAtom,
  drumSelectedSongAtom,
  drumGameResultAtom,
  drumUserTokenAtom,
  drumUserProfileAtom,
  drumUserPreferencesAtom,
  drumSelectedNoticeAtom,
  DEFAULT_DRUM_PREFERENCES,
} from '../shared/store/drumAppAtoms';
import { fetchPublicNotices } from '../shared/api/memberApi';
import { formatProfileDate, profileInitial } from '../shared/utils/drumHelpers';
import './Profile.scss';

function Profile() {
  const activePlan = useAtomValue(drumActivePlanAtom);
  const language = useAtomValue(drumLanguageAtom);
  const user = useAtomValue(drumUserProfileAtom);
  const setLoggedIn = useSetAtom(drumLoggedInAtom);
  const setSelectedSong = useSetAtom(drumSelectedSongAtom);
  const setGameResult = useSetAtom(drumGameResultAtom);
  const setUserToken = useSetAtom(drumUserTokenAtom);
  const setUserProfile = useSetAtom(drumUserProfileAtom);
  const setPreferences = useSetAtom(drumUserPreferencesAtom);
  const setSelectedNotice = useSetAtom(drumSelectedNoticeAtom);
  const navigate = useNavigate();
  const [latestNotice, setLatestNotice] = useState<NoticeItem | null>(null);

  const joinedDate = formatProfileDate(user?.joinedAt, language);
  const joined = joinedDate
    ? (language === 'ko' ? `${joinedDate} 가입` : `Joined ${joinedDate}`)
    : '';
  const plan = PASS_PLANS.find((item) => item.id === activePlan);
  const grade = plan ? (language === 'ko' ? `${plan.gradeKo} 등급` : `${plan.gradeEn} GRADE`) : (language === 'ko' ? '일반 등급' : 'FREE GRADE');

  useEffect(() => {
    let cancelled = false;
    fetchPublicNotices()
      .then((notices) => {
        if (!cancelled) setLatestNotice(notices[0] || null);
      })
      .catch(() => {
        if (!cancelled) setLatestNotice(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleLogout = () => {
    setLoggedIn(false);
    setUserToken('');
    setUserProfile(null);
    setPreferences(DEFAULT_DRUM_PREFERENCES);
    setSelectedSong(null);
    setGameResult(null);
  };

  const openLatestNotice = () => {
    if (!latestNotice) {
      navigate('/drum/profile/notices');
      return;
    }
    setSelectedNotice(latestNotice);
    navigate(`/drum/profile/notices/${latestNotice.id}`);
  };

  return (
    <div className="page profile-page">
      <section className="profile-hero">
        <div className="profile-avatar">
          {user?.picture ? <img src={user.picture} alt={user.name} /> : <span>{profileInitial(user?.name)}</span>}
        </div>
        <div>
          <span className="eyebrow">PLAYER PROFILE</span>
          <h1>{user?.name || (language === 'ko' ? '회원' : 'Player')}</h1>
          <p>{[user?.email, joined].filter(Boolean).join(' · ')}</p>
          <span className={`profile-access ${plan ? 'active' : 'free'}`}>{grade}</span>
        </div>
        <button onClick={() => navigate('/drum/subscription')}><Crown weight="fill" /> {language === 'ko' ? '드럼하이 패스 보기' : 'VIEW DRUM Hi PASS'}</button>
      </section>
      <div className="profile-grid">
        <section>
          <h2>Account</h2>
          <button onClick={() => navigate('/drum/profile/details')}><User /> Profile details <CaretRight /></button>
          <button onClick={() => navigate('/drum/profile/notifications')}><Bell /> Notifications <CaretRight /></button>
          <button onClick={() => navigate('/drum/profile/settings')}><Gear /> App settings <CaretRight /></button>
        </section>
        <section>
          <h2>Support</h2>
          <button onClick={() => navigate('/drum/profile/notices')}><MusicNotes /> Notices <CaretRight /></button>
          <button onClick={() => navigate('/drum/profile/help')}><Headphones /> Help center <CaretRight /></button>
          <button onClick={handleLogout}><SignOut /> Sign out <CaretRight /></button>
        </section>
      </div>
      {latestNotice && (
        <section className="notice-strip">
          <span>NEW</span>
          <div><strong>{latestNotice.title}</strong><p>{latestNotice.summary}</p></div>
          <button onClick={openLatestNotice}>Read notice</button>
        </section>
      )}
    </div>
  );
}

export default Profile;
